import { formatNumToStandardNotation } from '@modules/common/utils';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import { getTooltipTitleForFormulaValueCell } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/getTooltipTitleForFormulaValueCell';
import { renderFeedbackCountHeader } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/renderFeedbackCountHeader';
import { QuantifyState } from '@modules/quantify/store/types';
import { Link, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { EqlField } from 'generated/graphql';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface getMultiRecordQueryValueColumnParam {
  onFeedbackCountClick: (params: ShowFeedbackDrawerFromTableOptions) => void;
  headerName: string;
  formulaID?: EQLFunctionName;
  groupBy: QuantifyState['query']['groupByField'];
  isCompactTableVariant?: boolean;
  measuredAsCriteria: EqlField | undefined;
  shouldRemoveFileUploadPrefix: boolean;
  isUnifiedFieldEnabled: boolean;
}

export function getMultiRecordQueryValueColumn({
  onFeedbackCountClick,
  headerName,
  formulaID,
  groupBy,
  isCompactTableVariant,
  measuredAsCriteria,
  shouldRemoveFileUploadPrefix,
  isUnifiedFieldEnabled,
}: getMultiRecordQueryValueColumnParam): GridColDef {
  const renderValueCell = (params: GridRenderCellParams<any, number>) => {
    if (!params.value) {
      return params.value;
    }

    return (
      <Tooltip
        arrow
        title={getTooltipTitleForFormulaValueCell({
          groupBy,
          feedbackCount: params.value,
          displayName: params.row.label,
          formulaID,
          params,
          measuredAsCriteria,
          shouldRemoveFileUploadPrefix,
          isUnifiedFieldEnabled,
        })}
        placement="top"
        disableInteractive
      >
        <Link
          component="button"
          variant="body1"
          onClick={(event) => {
            event.stopPropagation();
            onFeedbackCountClick({
              id: params.row.id,
              label: params.row.label,
              category: params.row.category,
              isInvisible: params.row.isInvisible,
              isUpdateInProgress: params.row.isUpdateInProgress,
            });
          }}
          color="primary"
          underline="hover"
        >
          {formatNumToStandardNotation(params.value)}
        </Link>
      </Tooltip>
    );
  };

  return {
    field: 'value',
    headerName,
    minWidth: isCompactTableVariant ? 100 : 175,
    flex: 1,
    type: 'number',
    renderCell: renderValueCell,
    sortingOrder: ['asc', 'desc'],
    renderHeader: () =>
      renderFeedbackCountHeader({
        isNormalised: formulaID === EQLFunctionName.NormaliseByPercent,
        fieldDisplayName: groupBy?.displayName,
        headerName,
      }),
  };
}
